/**
 * Menschen beim Namen nennen — nicht bei der Adresse.
 *
 * Anlass (Alex, 19.08., Screenshot 119828): Über seiner eigenen Nachricht stand
 * `APOPOVIC.AUT@GMAIL.COM`. Die Zuschreibung SOLL da stehen — sie belegt, wer
 * gesendet hat, und genau darum ging es ihm im RBAC-Sinn. Aber eine
 * E-Mail-Adresse ist ein Anmeldename, keine Anrede: Sie gehört nicht in eine
 * Oberfläche, in der Menschen einander lesen.
 *
 * Auflösung in drei Stufen, absteigend nach Verlässlichkeit:
 *   1. Der angemeldete Nutzer selbst (inkl. seiner Alias-Adressen) — ohne
 *      Netzabfrage, immer verfügbar, deckt den häufigsten Fall ab.
 *   2. Das Nutzerverzeichnis der auth-api dieser Instanz — nur einmal geladen,
 *      nur wenn der Betrachter es lesen darf (sonst 403, dann eben Stufe 3).
 *   3. Die Adresse selbst — und zwar unverändert, NICHT geraten. Lieber eine
 *      technische Kennung als ein falscher Name über einer Nachricht.
 *
 * Kein Aufruf auf Verdacht: Das Verzeichnis wird erst geholt, wenn eine
 * Zuschreibung auftaucht, die Stufe 1 nicht auflöst.
 */
import { serviceOrigin } from './services';
import { authHeader } from '../api/client';
import { instanceReady } from '../hooks/useInstanceMode';

interface DirectoryEntry { email?: string; first_name?: string; last_name?: string }

const names = new Map<string, string>();   // lowercase email → Anzeigename
let selfNames: Map<string, string> = new Map();
let directoryLoaded = false;
let directoryInFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const displayName = (u: DirectoryEntry): string =>
  [u.first_name, u.last_name].filter(Boolean).join(' ').trim();

/** Der angemeldete Nutzer — vom Portal einmal gesetzt, deckt Stufe 1 ab. */
export function setSelfIdentity(emails: string[], name: string) {
  if (!name.trim()) return;
  const next = new Map<string, string>();
  for (const e of emails) if (e) next.set(e.toLowerCase(), name);
  selfNames = next;
  listeners.forEach((l) => l());
}

async function loadDirectory(): Promise<void> {
  if (directoryLoaded) return;
  if (directoryInFlight) return directoryInFlight;
  directoryInFlight = (async () => {
    try {
      await instanceReady();
      const base = serviceOrigin('auth');
      if (!base) return;
      const res = await fetch(`${base}/api/v1/auth/users`, { headers: authHeader() });
      if (!res.ok) return;                       // 403 für Nicht-Admins ist normal
      const body = await res.json() as DirectoryEntry[] | { users?: DirectoryEntry[] };
      const list = Array.isArray(body) ? body : (body.users || []);
      for (const u of list) {
        const n = displayName(u);
        if (u.email && n) names.set(u.email.toLowerCase(), n);
      }
    } catch { /* offline / kein Zugriff → Stufe 3 */ }
    finally {
      directoryLoaded = true;
      directoryInFlight = null;
      listeners.forEach((l) => l());
    }
  })();
  return directoryInFlight;
}

/**
 * Anzeigename zu einer Zuschreibung. Gibt die Eingabe unverändert zurück, wenn
 * kein Name bekannt ist — und stößt dabei das einmalige Laden des Verzeichnisses
 * an, damit die nächste Darstellung besser ist.
 */
export function personName(idOrEmail: string | null | undefined): string {
  const raw = (idOrEmail || '').trim();
  if (!raw) return '';
  const key = raw.toLowerCase();
  const self = selfNames.get(key);
  if (self) return self;
  const known = names.get(key);
  if (known) return known;
  if (raw.includes('@')) void loadDirectory();
  return raw;
}

/** Für React: neu rendern, sobald das Verzeichnis oder die eigene Identität da ist. */
export function subscribePeople(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function peopleVersion(): number {
  return (directoryLoaded ? 1 : 0) + names.size + selfNames.size;
}
